import type { ReactElement } from 'react'
import { AppShell } from '../components/AppShell.js'
import { Button } from '../components/ui/button.js'
import { Icon } from '../components/installer/icons.js'
import { PROOF_STEPS } from '../lib/proofSteps.js'
import { nextUncapturedIdx, useDemoStore } from '../state/store.js'
import { useStep } from '../state/useStep.js'

/**
 * Good-path verification result for the just-captured proof step. The current
 * photo is already in `state.captured` (dispatched by CaptureScreen before
 * navigating here), so `nextUncapturedIdx` points at the next pending step —
 * or -1 when all five are done, which routes to the summary.
 *
 * Ported from the Lovable prototype `Index.tsx` `screen-verified` (lines 644-686).
 */
export const VerifyPassedScreen = (): ReactElement | null => {
  const { state, dispatch } = useDemoStore()
  const { goto } = useStep()

  const activeStep = PROOF_STEPS[state.activeIdx]
  if (activeStep === undefined) return null

  const next = nextUncapturedIdx(state)
  const nextStep = next === -1 ? undefined : PROOF_STEPS[next]
  // For a video shot, freeze the clip's poster — the AR box is already burned into
  // it, so no overlay is drawn (`passBox` stays undefined). For a still shot, show
  // the photo with the SAME coordinates as the camera screen (`cameraBox`) so the
  // box lands on the same feature as it did live.
  const verifyImage = activeStep.video !== undefined ? activeStep.video.poster : activeStep.basePhoto
  const passBox = activeStep.video !== undefined ? undefined : activeStep.cameraBox[0]

  const progress = (30 + ((state.activeIdx + 1) / PROOF_STEPS.length) * 60) / 100

  const footer =
    nextStep === undefined ? (
      <Button size="xl" onClick={() => goto('summary')}>
        Continue to Summary <Icon.Chevron />
      </Button>
    ) : (
      <Button
        size="xl"
        onClick={() => {
          dispatch({ type: 'START_PHOTO', idx: next })
          goto('capture')
        }}
      >
        Next: {nextStep.num} · {nextStep.label} <Icon.Chevron />
      </Button>
    )

  return (
    <AppShell
      title="Verification passed"
      showBack
      onBack={() => goto('capture')}
      progress={progress}
      footer={footer}
    >
      <div className="installer-surface page fade-in">
        {activeStep.subShots !== undefined ? (
          // Multi-capture summary (condition scan): the three captured proofs in capture
          // order — front panels, top edges, rear corners.
          <div className="verify-summary">
            {[0, 1, 2]
              .map((i) => activeStep.subShots?.[i])
              .filter((s): s is NonNullable<typeof s> => s !== undefined)
              .map((s) => (
                <div className="verify-summary-item" key={s.id}>
                  <div
                    className="verify-summary-thumb"
                    style={{ backgroundImage: `url(${s.video.poster})` }}
                  />
                  <div className="verify-summary-cap">
                    <Icon.Check /> {s.summaryLabel}
                  </div>
                </div>
              ))}
          </div>
        ) : (
          <div className="verify-image-card">
            <div className="verify-image" style={{ backgroundImage: `url(${verifyImage})` }}>
              {passBox !== undefined && (
                <div
                  className={`detection-box-live${passBox.tone === 'flag' ? ' flag' : ''}`}
                  style={{
                    top: passBox.top,
                    left: passBox.left,
                    width: passBox.width,
                    height: passBox.height,
                  }}
                >
                  <div className="detection-label-live">{passBox.text}</div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="signal-chip">
          <div className="signal-dot" />
          Verified
        </div>

        <div className="outcome-card">
          <div className="outcome-title">{activeStep.verify.outcomeTitle}</div>
          <div className="outcome-body">{activeStep.verify.outcomeBody}</div>
        </div>

        {activeStep.compare !== undefined && (
          <div className="compare-card">
            <div className="compare-caption">{activeStep.compare.caption}</div>
            <div className="compare-grid">
              <div className="compare-cell">
                <div
                  className="compare-thumb"
                  style={{ backgroundImage: `url(${activeStep.compare.left.thumb})` }}
                />
                <div className="compare-cell-label">{activeStep.compare.left.label}</div>
                <div className="compare-cell-value">{activeStep.compare.left.value}</div>
              </div>
              <div className="compare-op">≥</div>
              <div className="compare-cell">
                <div
                  className="compare-thumb"
                  style={{ backgroundImage: `url(${activeStep.compare.right.thumb})` }}
                />
                <div className="compare-cell-label">{activeStep.compare.right.label}</div>
                <div className="compare-cell-value">{activeStep.compare.right.value}</div>
              </div>
            </div>
            <div className="compare-verdict">
              <div className="signal-dot" />
              {activeStep.compare.verdict}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
